import {
  patchCanonicalPatientMetadata,
  patientMetadataFromResponse,
} from '../utils/emailAttachmentPatientMetadata';
import { isSamePageLocationChange } from '../util/samePageLocationChange';

const initialState = {
  recordData: [],
  emailAttachmentPatientLatestRequestIds: {},
};

const userSelectedNone = (data) => (
  data && data.length > 0 && data[0] === 'none'
);

const userhadAllPreviouslySelected = ({ recordData }) => (
  recordData && recordData.length > 0 && recordData[0] === 'all'
);

const inboxTable = (state = initialState, action) => {
  switch (action.type) {
    case 'USER_LOGGED_OUT':
      return initialState;
    case 'SELECTED_ROWS': {
      // On the received records page, when select all checkbox is unchecked, the payload
      // is an array with 'none' as the only element.
      //
      // In this case, we want to set the data to an empty array
      //
      // There is also a longstanding bug in v0 of material-ui that does not handle
      // de-selecting rows if "all" are selected. See: https://github.com/mui-org/material-ui/issues/5964
      //
      // As a result, if "all" is currently selected and anything else is selected outside,
      // we will not return the selected data and instead reset the selected rows.
      if (userSelectedNone(action.payload) || userhadAllPreviouslySelected(state)) {
        return { ...state, recordData: [] };
      }

      // If it's array, no need to convert object to array. We need to check this because inbox
      // pages expect the recordData to be of type array, but when updating the patient info, it
      // returns an object.
      if (!Array.isArray(action.payload)) {
        return {
          ...state,
          recordData: [action.payload],
        };
      }

      return {
        ...state,
        // The payload will consist of the Row Data from the Inbox Table for the
        // specific rows selected.
        recordData: action.payload,
      };
    }
    case 'FINISH_AUTOUPLOAD': {
      const upload = action.payload?.data?.emr_upload || action.payload?.data;
      if (!upload?.send_token) return state;

      // SMS opens from the selected row, which can outlive the refreshed inbox data.
      const savedRecord = upload.record || upload;
      const contactDetails = Object.fromEntries(
        ['patient_phone_number', 'provider_office', 'referring_provider']
          .filter((key) => savedRecord[key] !== undefined && savedRecord[key] !== null)
          .map((key) => [key, savedRecord[key]]),
      );
      return {
        ...state,
        recordData: state.recordData.map((record) => (
          record?.send_token === upload.send_token ? { ...record, ...contactDetails } : record
        )),
      };
    }
    case 'FINISHED_MERGE_RECORDS': {
      // After records are merged, selected data should be cleared
      return initialState;
    }
    case 'BEGIN_UPDATE_EMAIL_ATTACHMENT_PATIENT': {
      const { fileToken, requestId } = action.meta;
      const latestRequestId = state.emailAttachmentPatientLatestRequestIds?.[fileToken];
      if (latestRequestId !== undefined && latestRequestId >= requestId) return state;

      return {
        ...state,
        emailAttachmentPatientLatestRequestIds: {
          ...(state.emailAttachmentPatientLatestRequestIds || {}),
          [fileToken]: requestId,
        },
      };
    }
    case 'FINISHED_UPDATE_EMAIL_ATTACHMENT_PATIENT': {
      const { fileToken, requestId } = action.meta;
      if (state.emailAttachmentPatientLatestRequestIds?.[fileToken] !== requestId) return state;

      const canonicalMetadata = patientMetadataFromResponse(action.payload);
      if (!canonicalMetadata) return state;

      return {
        ...state,
        recordData: state.recordData.map((record) => (
          patchCanonicalPatientMetadata(record, fileToken, canonicalMetadata)
        )),
      };
    }
    case 'UPDATE_SELECTED_RECORD_LABELS': {
      const { fileToken, labels } = action.payload;
      return {
        ...state,
        recordData: state.recordData.map((record) => (
          record.file_token === fileToken
            ? { ...record, labels }
            : record
        )),
      };
    }
    case 'FINISHED_UPDATE_RECORD': {
      // After record is edited, selected data should be cleared
      return initialState;
    }
    // When user navigates between pages, make sure to not persist selected data
    // between the different record pages (i.e. from sent to received)
    case '@@router/LOCATION_CHANGE': {
      // The slideout viewer's own history entries are not a page change (ENG-3550).
      if (isSamePageLocationChange(action)) return state;
      return initialState;
    }
    default:
      return state;
  }
};

export default (state, action) => {
  const currentState = state || initialState;
  const nextState = inboxTable(currentState, action);
  if (action.type === 'USER_LOGGED_OUT'
    || action.type === 'BEGIN_UPDATE_EMAIL_ATTACHMENT_PATIENT'
    || nextState.emailAttachmentPatientLatestRequestIds
      === currentState.emailAttachmentPatientLatestRequestIds) return nextState;

  return {
    ...nextState,
    emailAttachmentPatientLatestRequestIds:
      currentState.emailAttachmentPatientLatestRequestIds || {},
  };
};
